import { AUDIO } from '../config/tuning';
import type { AudioEngine } from './AudioEngine';
import { AudioBus } from './AudioEngine';
import { distanceGain, dopplerRatio, occlusionCutoffHz, occlusionGain, propagationDelayS } from './Spatial';

/**
 * One-shot events: launches, detonations, flybys, impacts (SPEC §3.11).
 *
 * Every one is scheduled rather than played. A detonation 2 km away is heard 5.8 seconds after the
 * flash, so the sound is built now and started later — the M9 note is specific that this is "a
 * real, satisfying detail: you see the flash, then hear it", and doing it any other way collapses
 * two events into one.
 *
 * Nodes are created per event and disposed on `onended`. That is the opposite of the continuous
 * voices, and it is right for the same reason they are not: a detonation happens a few times a
 * minute, so the allocation is free, while holding a voice open for every possible explosion is
 * not. Web Audio nodes are also single-use — a stopped source cannot be restarted.
 */

export interface SpatialParams {
  /** Distance from the listener, metres. */
  readonly distanceM: number;
  /** Closing speed along the line of sight, m/s, positive when approaching. */
  readonly closingMs?: number;
  /** True when terrain blocks the path. */
  readonly occluded?: boolean;
}

export class AudioEvents {
  private readonly audio: AudioEngine;

  constructor(audio: AudioEngine) {
    this.audio = audio;
  }

  /**
   * A missile leaving the rail: a filtered noise burst with a long tail.
   *
   * SPEC §3.6.5 makes the launch visible before it is dangerous; this is the audio half of that
   * warning, and it is why the sound is long and low rather than a crack.
   */
  launch(spatial: SpatialParams): void {
    const nodes = this.beginNoise(spatial, AUDIO.launchNoiseS);
    if (nodes === null) return;
    const { ctx, filter, gain, at } = nodes;
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(AUDIO.launchCutoffHz, at);
    filter.frequency.exponentialRampToValueAtTime(180, at + AUDIO.launchNoiseS);
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(nodes.level, at + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + AUDIO.launchNoiseS);
    void ctx;
  }

  /**
   * A detonation: noise burst plus a low sine thump.
   *
   * The thump is what gives it size. Noise alone reads as static; the sine under it is the part
   * felt rather than heard, and it is what distinguishes a warhead from a twig snapping.
   */
  detonation(spatial: SpatialParams, yieldFraction = 1): void {
    const nodes = this.beginNoise(spatial, AUDIO.detonationS);
    if (nodes === null) return;
    const { ctx, filter, gain, at, level } = nodes;
    const scale = clamp01(yieldFraction) * 0.5 + 0.5;

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2600, at);
    filter.frequency.exponentialRampToValueAtTime(120, at + AUDIO.detonationS);
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(level * scale, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + AUDIO.detonationS);

    const bus = this.audio.bus(AudioBus.Effects);
    if (bus === null) return;
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(AUDIO.detonationThumpHz * 1.6, at);
    thump.frequency.exponentialRampToValueAtTime(AUDIO.detonationThumpHz * 0.6, at + 0.4);
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0, at);
    thumpGain.gain.linearRampToValueAtTime(level * scale * 1.4, at + 0.012);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, at + 0.55);
    thump.connect(thumpGain);
    thumpGain.connect(bus);
    thump.start(at);
    thump.stop(at + 0.6);
    thump.onended = (): void => {
      thump.disconnect();
      thumpGain.disconnect();
    };
  }

  /**
   * The Doppler sweep of a missile passing close.
   *
   * SPEC asks for this to be "unmistakable and memorable" — it is the sound of the near miss the
   * whole evasion model exists to produce. The sweep is driven from the actual closing speed, so a
   * fast crossing shift sounds different from a slow one rather than being a canned whoosh.
   */
  flyby(spatial: SpatialParams): void {
    const ctx = this.audio.ctx;
    const bus = this.audio.bus(AudioBus.Effects);
    const source = this.audio.createNoiseSource();
    if (ctx === null || bus === null || source === null) return;

    const level = this.levelFor(spatial);
    if (level <= 0) return;

    const at = ctx.currentTime + propagationDelayS(spatial.distanceM);
    const ratio = dopplerRatio(spatial.closingMs ?? 0);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 2.2;
    filter.frequency.setValueAtTime(AUDIO.flybyStartHz * ratio, at);
    filter.frequency.exponentialRampToValueAtTime(
      Math.max(80, AUDIO.flybyEndHz / Math.max(ratio, 0.2)),
      at + AUDIO.flybyS,
    );

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(level * 1.5, at + AUDIO.flybyS * 0.35);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + AUDIO.flybyS);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    source.start(at);
    source.stop(at + AUDIO.flybyS + 0.05);
    source.onended = (): void => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
  }

  /** Fragments striking the airframe: a short bright transient. */
  impact(intensity = 1): void {
    const nodes = this.beginNoise({ distanceM: 0 }, AUDIO.impactS);
    if (nodes === null) return;
    const { filter, gain, at, level } = nodes;
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(900, at);
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(level * clamp01(intensity), at + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + AUDIO.impactS);
  }

  /** Shared setup for every noise-based one-shot. Returns null when nothing can be heard. */
  private beginNoise(
    spatial: SpatialParams,
    durationS: number,
  ): { ctx: AudioContext; filter: BiquadFilterNode; gain: GainNode; at: number; level: number } | null {
    const ctx = this.audio.ctx;
    const bus = this.audio.bus(AudioBus.Effects);
    const source = this.audio.createNoiseSource();
    if (ctx === null || bus === null || source === null) return null;

    const level = this.levelFor(spatial);
    if (level <= 0) return null;

    const at = ctx.currentTime + propagationDelayS(spatial.distanceM);

    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    // Occlusion is a second low-pass in series: terrain takes the top off before it takes level,
    // which is why a site behind a ridge sounds distant rather than merely quiet.
    const occlusion = ctx.createBiquadFilter();
    occlusion.type = 'lowpass';
    occlusion.frequency.value = occlusionCutoffHz(spatial.occluded === true);

    source.connect(filter);
    filter.connect(occlusion);
    occlusion.connect(gain);
    gain.connect(bus);
    source.start(at);
    source.stop(at + durationS + 0.05);
    source.onended = (): void => {
      source.disconnect();
      filter.disconnect();
      occlusion.disconnect();
      gain.disconnect();
    };

    return { ctx, filter, gain, at, level };
  }

  private levelFor(spatial: SpatialParams): number {
    return distanceGain(spatial.distanceM) * occlusionGain(spatial.occluded === true);
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
